/**
 * Pure, React-free helpers for the Super Admin "Mark Position Flat"
 * operator-recovery workflow.
 *
 * Backend contract (POST /strategy/admin/deployments/{id}/mark-flat):
 *  - A missing body is `INSPECT` and never carries an Idempotency-Key.
 *  - Explicit `CLEANUP_THEN_MARK_FLAT` / `MARK_FLAT_ONLY` require a stable
 *    `Idempotency-Key` header; the key identifies one exact operation.
 *  - Responses carry normalized bounded decision fields only; provider
 *    payloads and raw broker evidence never reach this layer.
 *
 * Idempotency-key lifecycle (component memory only — never persisted,
 * never displayed, never logged):
 *  - created once when an explicit operation begins;
 *  - a retry of the SAME action after an uncertain/failed request reuses
 *    the SAME key so the backend resumes that exact operation;
 *  - a different explicit action uses its own key;
 *  - a terminal backend response for the deployment (MARKED_FLAT from any
 *    source, or CLEANUP_INCOMPLETE) clears ALL stored explicit-action keys
 *    for that deployment, so a later recovery episode can never replay a
 *    stale historical terminal operation with an old key;
 *  - errors (including 409 OPERATOR_RECOVERY_IN_PROGRESS) retain the key
 *    while the dialog stays open for the same-action retry;
 *  - "Do Nothing" abandons the frontend recovery episode: it sends zero
 *    requests, performs zero backend mutation, and clears the deployment's
 *    stored keys. If an uncertain operation is actually still IN_PROGRESS,
 *    the backend's one-IN_PROGRESS-per-deployment invariant safely rejects
 *    a fresh key until that operation resolves.
 */

export const MARK_FLAT_OUTCOMES = {
  MARKED_FLAT: 'MARKED_FLAT',
  DECISION_REQUIRED: 'DECISION_REQUIRED',
  CLEANUP_IN_PROGRESS: 'CLEANUP_IN_PROGRESS',
  CLEANUP_INCOMPLETE: 'CLEANUP_INCOMPLETE',
};

export const MARK_FLAT_ACTIONS = {
  INSPECT: 'INSPECT',
  CLEANUP_THEN_MARK_FLAT: 'CLEANUP_THEN_MARK_FLAT',
  MARK_FLAT_ONLY: 'MARK_FLAT_ONLY',
  // Do Nothing is a UI-only close: it never produces a request of any kind.
  DO_NOTHING: 'DO_NOTHING',
};

// Backend-accepted explicit request actions, in button display order.
const REQUESTABLE_ACTIONS = [MARK_FLAT_ACTIONS.CLEANUP_THEN_MARK_FLAT, MARK_FLAT_ACTIONS.MARK_FLAT_ONLY];

// ─── Idempotency keys ──────────────────────────────────────────────────────────

function hexFromBytes(bytes) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function generateIdempotencyKey() {
  // Must satisfy the backend contract ^[A-Za-z0-9._:-]{8,128}$.
  // Only a strong crypto source is used — never Math.random — because the
  // key identifies a mutating broker-recovery operation.
  const cryptoRef = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return String(cryptoRef.randomUUID());
  }
  if (cryptoRef && typeof cryptoRef.getRandomValues === 'function') {
    const bytes = new Uint8Array(24);
    cryptoRef.getRandomValues(bytes);
    return `mf-${hexFromBytes(bytes)}`;
  }
  throw new Error('SECURE_IDEMPOTENCY_KEY_SOURCE_UNAVAILABLE');
}

export function createIdempotencyKeyStore() {
  const keys = new Map();
  const storeKey = (deploymentId, action) => `${deploymentId}:${action}`;
  return {
    // Returns the existing key for the exact operation or creates one once.
    // A later call for the same (deploymentId, action) — e.g. a retry after a
    // timeout/network failure — must reuse the same key.
    getKey(deploymentId, action) {
      const k = storeKey(deploymentId, action);
      let value = keys.get(k);
      if (!value) {
        value = generateIdempotencyKey();
        keys.set(k, value);
      }
      return value;
    },
    // Terminal outcomes end the operation; the next explicit attempt for this
    // action is a genuinely new operation and must use a fresh key.
    release(deploymentId, action) {
      keys.delete(storeKey(deploymentId, action));
    },
    // Recovery-episode boundary: clears every stored explicit-action key for
    // the deployment (other deployments untouched) so a later episode can
    // never replay a stale historical terminal operation with an old key.
    releaseDeployment(deploymentId) {
      const prefix = `${deploymentId}:`;
      for (const k of [...keys.keys()]) {
        if (k.startsWith(prefix)) keys.delete(k);
      }
    },
  };
}

// ─── Request-variable controller seam ─────────────────────────────────────────
// AdminUserDetailPage uses these three methods to build every mark-flat
// mutation call, so tests can prove the exact request-variable shapes.

export function createMarkFlatController(store = createIdempotencyKeyStore()) {
  return {
    // Initial Mark FLAT click: inspection only — no action body, no Idempotency-Key.
    inspect(deploymentId) {
      return { deploymentId };
    },
    // Explicit Super Admin choice. The stable key is created once per exact
    // operation and reused for retries of the same uncertain operation.
    begin(deploymentId, action) {
      if (!REQUESTABLE_ACTIONS.includes(action)) {
        throw new Error('MARK_FLAT_ACTION_NOT_REQUESTABLE');
      }
      return {
        deploymentId,
        action,
        idempotencyKey: store.getKey(deploymentId, action),
      };
    },
    // Called with the backend response once the request settles (from any
    // source, including INSPECT). A terminal outcome — MARKED_FLAT or
    // CLEANUP_INCOMPLETE — ends the deployment's current episode and clears
    // ALL of its stored explicit-action keys, so a later recovery can never
    // replay a stale historical terminal operation with an old key. Failures
    // never call finish, so their key stays available for the same-action
    // retry while the dialog remains open.
    finish(deploymentId, response) {
      if (isTerminalOutcome(response)) {
        store.releaseDeployment(deploymentId);
      }
    },
    // "Do Nothing" episode boundary: abandon the current frontend recovery
    // episode. Sends zero requests, performs zero backend mutation, and
    // clears the deployment's stored keys — a later episode starts fresh. If
    // an earlier uncertain operation is actually still IN_PROGRESS, the
    // backend's one-IN_PROGRESS-per-deployment invariant safely rejects a new
    // key until that operation resolves.
    abandon(deploymentId) {
      store.releaseDeployment(deploymentId);
    },
  };
}

export function isTerminalOutcome(response) {
  const outcome = String(response?.outcome || '').toUpperCase();
  return (
    outcome === MARK_FLAT_OUTCOMES.MARKED_FLAT ||
    outcome === MARK_FLAT_OUTCOMES.CLEANUP_INCOMPLETE
  );
}

// ─── Response routing ──────────────────────────────────────────────────────────

export function resolveMarkFlatResponse(response) {
  const outcome = String(response?.outcome || '').toUpperCase();
  if (outcome === MARK_FLAT_OUTCOMES.MARKED_FLAT) {
    return { kind: 'success' };
  }
  if (outcome === MARK_FLAT_OUTCOMES.CLEANUP_INCOMPLETE) {
    return {
      kind: 'cleanup_incomplete',
      actions: requestableActions(response?.available_actions),
    };
  }
  if (outcome === MARK_FLAT_OUTCOMES.CLEANUP_IN_PROGRESS) {
    return {
      kind: 'cleanup_in_progress',
      actions: requestableActions(response?.available_actions),
    };
  }
  if (outcome === MARK_FLAT_OUTCOMES.DECISION_REQUIRED || response?.decision_required === true) {
    return {
      kind: 'decision',
      actions: requestableActions(response?.available_actions),
    };
  }
  // Unknown 2xx outcome per the contract — the page must not guess.
  return { kind: 'unknown' };
}

// Strictly the requestable actions the backend returned, in display order.
// DO_NOTHING is never requestable (UI-only close), and cleanup is never
// inferred from frontend data — only from the backend's available_actions.
export function requestableActions(availableActions) {
  const list = Array.isArray(availableActions) ? availableActions.map((a) => String(a)) : [];
  return REQUESTABLE_ACTIONS.filter((a) => list.includes(a));
}

// ─── User-facing copy (normalized fields only — never provider payloads) ───────

const VERIFICATION_NOTICES = {
  SESSION_UNAVAILABLE:
    'The broker session is currently unavailable, so broker cleanup cannot be verified or performed from this workflow.',
  VERIFICATION_UNAVAILABLE: 'Broker information could not be verified right now.',
  AMBIGUOUS_EVIDENCE: 'Broker evidence is ambiguous — the exact exposure cannot be safely identified.',
  CONFLICTING_EVIDENCE: 'Broker evidence is conflicting — the exact exposure cannot be safely identified.',
};

export const CLEANUP_INCOMPLETE_NOTICE =
  'Broker cleanup could not be fully verified. The deployment has not been marked flat by this cleanup operation.';

export const CLEANUP_IN_PROGRESS_NOTICE =
  'A Manual exit is awaiting broker confirmation. The deployment has not been marked flat yet. Recheck with the same cleanup action.';

export const UNKNOWN_OUTCOME_NOTICE =
  'The Mark Flat response could not be interpreted. No change was made.';

export function verificationNotice(verificationStatus, response) {
  const status = String(verificationStatus || '').toUpperCase();
  if (VERIFICATION_NOTICES[status]) return VERIFICATION_NOTICES[status];
  if (status.startsWith('ACTIONABLE')) {
    const evidence = [];
    if (response?.position_evidence_present) evidence.push('an open position');
    if (response?.pending_order_evidence_present) evidence.push('a pending order');
    const detail = evidence.length ? `: ${evidence.join(' and ')}` : '';
    return `AlgoSys identified relevant broker evidence for this deployment${detail}.`;
  }
  if (status === 'NO_RELEVANT_EXPOSURE') return 'No relevant broker exposure was found.';
  return 'Broker state could not be fully summarized.';
}

export const MARK_FLAT_ACTION_LABELS = {
  CLEANUP_THEN_MARK_FLAT: 'Exit/Cancel & Mark Flat',
  MARK_FLAT_ONLY: 'Mark Flat Only',
  DO_NOTHING: 'Do Nothing',
};

export const MARK_FLAT_ACTION_EXPLANATIONS = {
  CLEANUP_THEN_MARK_FLAT:
    'AlgoSys will attempt to exit or cancel only the exact broker exposure and orders it can safely identify, verify the result with the broker, and mark local state flat only after verification succeeds.',
  MARK_FLAT_ONLY:
    'No broker cleanup is performed. Broker positions and orders may intentionally remain untouched.',
  DO_NOTHING: 'Close without making any changes. No request is sent.',
};

// ─── Safe error mapping (interceptor-normalized error.message is a reason code) ─

const ERROR_NOTICES = {
  OPERATOR_RECOVERY_IN_PROGRESS: 'A recovery operation is already in progress for this deployment.',
  OPERATOR_RECOVERY_CLAIM_CONFLICT: 'A recovery operation is already in progress for this deployment.',
  MARK_FLAT_EVIDENCE_STALE: 'Position information changed while recovering. Please review the deployment and try again.',
  IDEMPOTENCY_KEY_INVALID: 'The recovery request could not be identified. Please try the action again.',
  IDEMPOTENCY_KEY_CONFLICT: 'The recovery request could not be identified. Please try the action again.',
  SUPER_ADMIN_REQUIRED: 'Only a Super Admin can perform this recovery action.',
  MARK_FLAT_RUNTIME_OWNER_REQUIRED: 'Recovery is temporarily unavailable. Please try again later.',
  MARK_FLAT_ACTION_INVALID: 'This recovery action is not available. Please refresh and try again.',
  MARK_FLAT_OWNER_UNAVAILABLE: 'The deployment owner could not be resolved. Please refresh and try again.',
  DEPLOYMENT_NOT_FOUND: 'Deployment not found.',
  BROKER_RECOVERY_NOT_ENABLED: 'Broker recovery is not available for this deployment.',
  MARK_FLAT_BROKER_CONTEXT_UNAVAILABLE: 'Broker access is currently unavailable for this deployment.',
};

const GENERIC_ERROR_NOTICE = 'Mark Flat attempt failed. Please try again.';

export function markFlatErrorMessage(error) {
  // A network/timeout failure has no backend status — retrying the same
  // action (with the same key) is the safe next step.
  if (error && error.status == null) {
    return 'The request could not be confirmed due to a network issue. Retrying the same action continues the same recovery attempt.';
  }
  return ERROR_NOTICES[String(error?.message || '').trim()] || GENERIC_ERROR_NOTICE;
}

export function isRecoveryInProgressError(error) {
  return (
    error?.status === 409 &&
    String(error?.message || '').trim() === 'OPERATOR_RECOVERY_IN_PROGRESS'
  );
}
